/**
 * Marketplace público de sorteos benéficos.
 *
 * Lista cobros tipo `raffle_share` con `allow_anonymous=true` y `status=open`.
 * Sin login, sin filtros complicados — pura lista editorial.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import api from '../api/client';
import TopBar from '../components/TopBar';


interface PublicRaffle {
  slug: string;
  title: string;
  organizer_name: string;
  amount_per_share: number | null;
  expected_shares: number | null;
  paid_shares_count: number;
  total_paid_amount: number;
  progress_pct: number;
  deadline: string | null;
  public_url: string;
  created_at: string;
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function PublicRafflesPage() {
  const [list, setList] = useState<PublicRaffle[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Sorteos benéficos abiertos · Pagafácil';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'Directorio público de sorteos benéficos legales abiertos. Apoya a una hermandad, peña, AMPA u ONG comprando tu papeleta con DNI nominativo.',
    );
    setMeta('theme-color', '#1C5D82');

    api.get('/public/group-buys/marketplace/sorteos')
      .then((r) => setList(r.data.raffles))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Sorteos abiertos" />

      {/* Hero */}
      <section>
        <div className="max-w-5xl mx-auto px-6 pt-16 md:pt-20 pb-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
            · Sorteos benéficos abiertos
          </div>
          <h1 className="font-display font-bold text-[44px] sm:text-[54px] lg:text-[60px] leading-[1.0] tracking-[-0.02em] text-ink mb-5 max-w-[820px]">
            Compra papeleta. Echa una mano.
          </h1>
          <p className="text-[17px] leading-relaxed text-ink-700 max-w-[640px] mb-4">
            Hermandades, peñas, AMPAs y ONG que tienen un sorteo benéfico abierto
            ahora mismo. Tu papeleta queda a tu nombre con DNI vinculante (Decreto
            325/1988 Andalucía · Ley 6/2001 Madrid).
          </p>
          <p className="text-[14px] text-ink-700 max-w-[640px]">
            Si tu organización quiere aparecer aquí, crea tu sorteo con el toggle
            <strong className="text-ink"> "Visible en marketplace público"</strong> activado.
          </p>
        </div>
      </section>

      {/* Listado */}
      <section className="border-t border-line">
        <div className="max-w-5xl mx-auto px-6 py-12">
          {loading ? (
            <p className="text-ink-500 font-mono text-[12px] uppercase tracking-[0.14em]">
              · Un momento…
            </p>
          ) : list && list.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {list.map((r) => (
                <RaffleCard key={r.slug} r={r} />
              ))}
            </div>
          ) : (
            <div className="border-[1.5px] border-dashed border-line-strong rounded-card p-10 text-center bg-paper">
              <div
                className="mx-auto mb-4 w-12 h-12 rounded-xl"
                style={{ background: '#F4EEE3', border: '1.5px solid #E5DCCD' }}
                aria-hidden
              />
              <h2 className="font-display font-semibold text-[22px] text-ink tracking-[-0.01em] mb-2">
                Sin sorteos abiertos por ahora.
              </h2>
              <p className="text-[14px] text-ink-700 mb-6">
                Vuelve en unos días o cuéntale a tu hermandad / AMPA que aquí
                puede montar el suyo.
              </p>
              <Link
                to="/nuevo"
                className="inline-block px-6 py-3 rounded-xl bg-brand text-paper font-semibold text-[14.5px] shadow-btn hover:bg-brand-700 transition"
              >
                Crear un sorteo benéfico →
              </Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}


function RaffleCard({ r }: { r: PublicRaffle }) {
  return (
    <a
      href={r.public_url}
      className="block bg-paper border border-line shadow-card rounded-card p-6 hover:shadow-elevated transition"
    >
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · Sorteo benéfico
        </div>
        {r.deadline && (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-amberCC-700">
            Cierra{' '}
            {new Date(r.deadline).toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'short',
            })}
          </span>
        )}
      </div>
      <h2 className="font-display font-bold text-[22px] text-ink leading-tight tracking-[-0.01em] mb-1">
        {r.title}
      </h2>
      <p className="text-[12.5px] text-ink-500 mb-4">
        Organiza: <span className="text-ink-700">{r.organizer_name}</span>
      </p>

      {r.expected_shares ? (
        <>
          <div className="flex items-baseline justify-between text-[12.5px] text-ink-700 mb-1.5">
            <span>
              {r.paid_shares_count} / {r.expected_shares} papeletas vendidas
            </span>
            <span className="font-semibold text-olive-700">
              {formatEUR(r.total_paid_amount)}
            </span>
          </div>
          <div className="h-2 bg-line rounded-pill overflow-hidden mb-5">
            <div
              className="h-full bg-olive rounded-pill"
              style={{ width: `${r.progress_pct}%` }}
            />
          </div>
        </>
      ) : null}

      <div className="flex items-center justify-between border-t border-line-soft pt-4 mt-1">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            · Cada papeleta
          </div>
          <div className="font-display font-semibold text-[20px] text-ink leading-none">
            {r.amount_per_share != null ? formatEUR(r.amount_per_share) : '—'}
          </div>
        </div>
        <span className="px-5 py-2.5 rounded-xl bg-brand text-paper text-[13px] font-semibold shadow-btn">
          Comprar mi papeleta →
        </span>
      </div>
    </a>
  );
}


function Mark({ size = 22 }: { size?: number }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background: 'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
        boxShadow: 'inset 0 0 0 4px #FFFFFF',
      }}
      aria-hidden
    />
  );
}
